import { INVITATION_ID, INVITATION_LINK_ID } from '../../components/org/constants/invitation'
import { LOGGED_OUT_ONLY_PATHS } from '@dtx-company/true-common/src/constants/layout'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { UNIFIED_ASSET_MANAGEMENT } from '@dtx-company/true-common/src/constants/featureFlags'
import { useAuthState } from '../../hooks/useAuthState'
import { useCallback, useEffect, useState } from 'react'
import { useFlowFeature } from '../useFlowFeature/useFlowFeature'
import { useHandleFlowcodeUserRedirect } from '../useHandleFlowcodeUserRedirect'
import { useIsAccountPaused } from '../useIsAccountPaused'
import { useRedirectToPausedAccountPage } from '../useRedirectToPausedAccountPage'
import { useRouter } from 'next/router'

const ssoRoutes = [Routes.SIGN_IN, Routes.SIGN_UP, Routes.ACCEPT_INVITE]

export function useRedirectOnAuthChanges(
  authRequired = false,
  doNotRedirectOnAuthChange = false,
  shouldRedirectToSignUp = false,
  unAuthenticatedRedirectTo = ''
): void {
  const router = useRouter()
  const currentPageIsSSORoute =
    router.pathname === Routes.ACCEPT_INVITE ? false : ssoRoutes.includes(router.pathname as Routes)
  const currentPageIsSSOLogin = router.pathname === Routes.SSO_LOGIN
  const handleFlowcodeUserRedirect = useHandleFlowcodeUserRedirect()
  const redirectTo = useRedirectTo()
  const isAccountPaused = useIsAccountPaused()
  const redirectToPausedAccountPage = useRedirectToPausedAccountPage()
  const [redirectCalled, setRedirectCalled] = useState(false)
  const { isAuthenticated, jwt, isAuthChecked } = useAuthState()

  const orgStatus = jwt?.org?.status
  const invitationId = (router.query.invitationId ?? '')?.toString()
  const invitationLinkId = (router.query.inviteId ?? '')?.toString()

  const [isUnifiedAssetManagementEnabled] = useFlowFeature(UNIFIED_ASSET_MANAGEMENT, {
    autoUpdate: true
  })

  useEffect(() => {
    if (!isAuthChecked) return
    if (redirectCalled) return
    if (doNotRedirectOnAuthChange) return
    if (isAuthenticated && currentPageIsSSORoute) {
      handleFlowcodeUserRedirect()
      setRedirectCalled(true)
    } else if (!isAuthenticated && unAuthenticatedRedirectTo) {
      window.location.href = unAuthenticatedRedirectTo
      setRedirectCalled(true)
    } else if (authRequired && !isAuthenticated) {
      if (invitationId && router.pathname === Routes.ACCEPT_INVITE) {
        localStorage.setItem(INVITATION_ID, invitationId?.toString())
      }
      if (invitationLinkId && router.pathname === Routes.ACCEPT_ORG_INVITE) {
        localStorage.setItem(INVITATION_LINK_ID, invitationLinkId?.toString())
      }
      if (shouldRedirectToSignUp) {
        redirectTo({ pathname: Routes.SIGN_UP, replace: true })
      } else {
        redirectTo({ pathname: Routes.SIGN_IN, replace: true })
      }
      setRedirectCalled(true)
    } else if (isAccountPaused && authRequired) {
      redirectToPausedAccountPage()
      setRedirectCalled(true)
    } else if (isAuthenticated) {
      if (LOGGED_OUT_ONLY_PATHS.includes(router.pathname as Routes)) {
        router.push(isUnifiedAssetManagementEnabled ? Routes.ASSETS : Routes.CODES)
        setRedirectCalled(true)
      }
    }
  }, [
    redirectCalled,
    setRedirectCalled,
    currentPageIsSSOLogin,
    isAuthChecked,
    isAuthenticated,
    router,
    handleFlowcodeUserRedirect,
    currentPageIsSSORoute,
    authRequired,
    redirectTo,
    doNotRedirectOnAuthChange,
    orgStatus,
    invitationId,
    invitationLinkId,
    shouldRedirectToSignUp,
    isAccountPaused,
    redirectToPausedAccountPage,
    unAuthenticatedRedirectTo,
    isUnifiedAssetManagementEnabled
  ])
}

function useRedirectTo(): ({ pathname, replace }: { pathname: string; replace?: boolean }) => void {
  const router = useRouter()
  return useCallback(
    ({ pathname, replace }) => {
      router[replace ? 'replace' : 'push']({
        pathname,
        query: { ...router.query, redirectTo: router.asPath }
      })
    },
    [router]
  )
}
